import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
    getNotifications,
    getUnreadNotificationsCount,
    markAllNotificationsRead,
    markNotificationRead,
} from "./thunk";

export interface NotificationMeta {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
}

export interface NotificationState {
    items: any[];
    meta: NotificationMeta;
    loading: {
        list: boolean;
        markAllRead: boolean;
        unreadCount: boolean;
        markRead: Record<string, boolean>;
    };
    unreadCount: number;
    error: string | null;
}

const parseNumber = (value: any): number | undefined => {
    if (typeof value === "number" && !Number.isNaN(value)) {
        return value;
    }

    if (typeof value === "string") {
        const parsed = Number(value);
        if (!Number.isNaN(parsed)) {
            return parsed;
        }
    }

    return undefined;
};

const parseBoolean = (value: any): boolean | undefined => {
    if (typeof value === "boolean") {
        return value;
    }

    if (typeof value === "number") {
        if (value === 1) {
            return true;
        }

        if (value === 0) {
            return false;
        }
    }

    if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (["true", "1", "yes", "y", "on", "read", "seen"].includes(normalized)) {
            return true;
        }

        if (["false", "0", "no", "n", "off", "unread"].includes(normalized)) {
            return false;
        }
    }

    return undefined;
};

const extractNotifications = (payload: any): any[] => {
    if (!payload) {
        return [];
    }

    if (Array.isArray(payload)) {
        return payload;
    }

    const candidates = [
        payload.notifications,
        payload.items,
        payload.results,
        payload.data,
        payload.data?.data,
        payload.payload,
    ];

    for (const candidate of candidates) {
        if (Array.isArray(candidate)) {
            return candidate;
        }
    }

    return [];
};

const extractSingleNotification = (payload: any): any | undefined => {
    if (!payload) {
        return undefined;
    }

    if (Array.isArray(payload) && payload.length > 0) {
        return payload[0];
    }

    const candidates = [payload.notification, payload.item, payload.data, payload.payload];
    for (const candidate of candidates) {
        if (!candidate) {
            continue;
        }

        if (Array.isArray(candidate) && candidate.length > 0) {
            return candidate[0];
        }

        if (typeof candidate === "object") {
            return candidate;
        }
    }

    if (typeof payload === "object") {
        return payload;
    }

    return undefined;
};

const deriveMetaFromPayload = (
    payload: any,
    previousMeta: NotificationMeta,
    listLength: number,
    params?: Record<string, any>
): NotificationMeta => {
    const meta: NotificationMeta = { ...previousMeta };
    let hasNextDefined = false;
    let hasPreviousDefined = false;

    const paramPage = parseNumber(
        params?.page ?? params?.pageNumber ?? params?.page_index ?? params?.currentPage ?? params?.pageNo
    );
    if (paramPage !== undefined) {
        meta.page = paramPage;
    }

    const paramPageSize = parseNumber(
        params?.pageSize ?? params?.perPage ?? params?.per_page ?? params?.limit ?? params?.page_size
    );
    if (paramPageSize !== undefined) {
        meta.pageSize = paramPageSize;
    }

    const metaSources = [
        payload,
        payload?.data,
        payload?.meta,
        payload?.data?.meta,
        payload?.pagination,
        payload?.data?.pagination,
    ];
    for (const source of metaSources) {
        if (!source || typeof source !== "object") {
            continue;
        }

        const page = parseNumber(
            source.page ??
                source.current_page ??
                source.currentPage ??
                source.pageNumber ??
                source.page_index ??
                source.pageNo
        );
        if (page !== undefined) {
            meta.page = page;
        }

        const pageSize = parseNumber(
            source.pageSize ?? source.perPage ?? source.per_page ?? source.limit ?? source.page_size
        );
        if (pageSize !== undefined) {
            meta.pageSize = pageSize;
        }

        const total = parseNumber(
            source.total ?? source.totalItems ?? source.totalCount ?? source.count ?? source.total_records
        );
        if (total !== undefined) {
            meta.total = total;
        }

        const totalPages = parseNumber(source.totalPages ?? source.lastPage ?? source.last_page ?? source.total_pages);
        if (totalPages !== undefined) {
            meta.totalPages = totalPages;
        }

        const hasNext = parseBoolean(
            source.hasNext ??
                source.hasMore ??
                source.has_next ??
                source.more ??
                (source.nextPage !== undefined ? true : undefined)
        );
        if (hasNext !== undefined) {
            meta.hasNext = hasNext;
            hasNextDefined = true;
        }

        const hasPrevious = parseBoolean(
            source.hasPrevious ??
                source.hasPrev ??
                source.has_previous ??
                (source.previousPage !== undefined ? true : undefined)
        );
        if (hasPrevious !== undefined) {
            meta.hasPrevious = hasPrevious;
            hasPreviousDefined = true;
        }
    }

    if (!meta.total || meta.total < listLength) {
        meta.total = listLength;
    }

    if (!meta.pageSize || meta.pageSize < 1) {
        meta.pageSize = previousMeta.pageSize;
    }

    if (!meta.totalPages || meta.totalPages < 1) {
        const computedTotal = meta.total && meta.pageSize ? Math.ceil(meta.total / meta.pageSize) : 0;
        meta.totalPages = computedTotal > 0 ? computedTotal : 1;
    }

    if (!hasNextDefined) {
        meta.hasNext = meta.page < meta.totalPages;
    }

    if (!hasPreviousDefined) {
        meta.hasPrevious = meta.page > 1;
    }

    return meta;
};

const ensureNotificationMarkedAsRead = (notification: any) => {
    if (!notification || typeof notification !== "object") {
        return;
    }

    const possibleReadFlags = ["isRead", "read", "seen", "status"];
    for (const flag of possibleReadFlags) {
        if (flag in notification) {
            (notification as any)[flag] = true;
        }
    }

    const timestampFields = ["readAt", "read_at", "seenAt", "seen_at"];
    const nowIso = new Date().toISOString();
    for (const field of timestampFields) {
        if ((notification as any)[field] === undefined || (notification as any)[field] === null) {
            (notification as any)[field] = nowIso;
        }
    }
};

const defaultMeta: NotificationMeta = {
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrevious: false,
};

export const initialState: NotificationState = {
    items: [],
    meta: { ...defaultMeta },
    loading: {
        list: false,
        markAllRead: false,
        unreadCount: false,
        markRead: {},
    },
    unreadCount: 0,
    error: null,
};

const notificationsSlice = createSlice({
    name: "notifications",
    initialState,
    reducers: {
        resetNotificationsState: (state) => {
            state.items = [];
            state.meta = { ...defaultMeta };
            state.loading = {
                list: false,
                markAllRead: false,
                unreadCount: false,
                markRead: {},
            };
            state.unreadCount = 0;
            state.error = null;
        },
        setNotificationsPageSize: (state, action: PayloadAction<number>) => {
            const value = parseNumber(action.payload);
            if (!value || value < 1) {
                return;
            }

            state.meta.pageSize = value;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getNotifications.pending, (state, action) => {
            state.loading.list = true;
            state.error = null;

            const params = action.meta.arg as Record<string, any> | undefined;
            if (params) {
                const requestedPage = parseNumber(
                    params.page ?? params.pageNumber ?? params.page_index ?? params.currentPage ?? params.pageNo
                );
                if (requestedPage !== undefined) {
                    state.meta.page = requestedPage;
                }

                const requestedPageSize = parseNumber(
                    params.pageSize ?? params.perPage ?? params.per_page ?? params.limit ?? params.page_size
                );
                if (requestedPageSize !== undefined) {
                    state.meta.pageSize = requestedPageSize;
                }
            }
        });
        builder.addCase(getNotifications.fulfilled, (state, action) => {
            state.loading.list = false;
            const notifications = extractNotifications(action.payload);
            const params = action.meta.arg as Record<string, any> | undefined;
            const isAppend = Boolean(params?.append);
            state.items = isAppend ? [...state.items, ...notifications] : notifications;
            const unreadFromPayload = parseNumber((action.payload as any)?.unread_count ?? (action.payload as any)?.data?.unread_count);
            if (unreadFromPayload !== undefined) {
                state.unreadCount = unreadFromPayload;
            }
            state.meta = deriveMetaFromPayload(
                action.payload,
                state.meta,
                Array.isArray(state.items) ? state.items.length : 0,
                params
            );
        });
        builder.addCase(getNotifications.rejected, (state, action) => {
            state.loading.list = false;
            state.error = typeof action.error.message === "string"
                ? action.error.message
                : "Không thể tải danh sách thông báo.";
        });

        builder.addCase(markNotificationRead.pending, (state, action) => {
            const { notificationId } = action.meta.arg ?? {};
            if (notificationId !== undefined && notificationId !== null) {
                const key = String(notificationId);
                state.loading.markRead[key] = true;
            }
        });
        builder.addCase(markNotificationRead.fulfilled, (state, action) => {
            const { notificationId, response } = action.payload ?? {};
            const key = notificationId !== undefined && notificationId !== null ? String(notificationId) : undefined;
            if (key) {
                delete state.loading.markRead[key];
            }

            const updatedNotification = extractSingleNotification(response);
            if (!updatedNotification && !key) {
                return;
            }

            for (let index = 0; index < state.items.length; index += 1) {
                const current = state.items[index];
                const currentId = current && current.id !== undefined ? current.id : current?.uuid ?? current?._id;
                const currentKey = currentId !== undefined && currentId !== null ? String(currentId) : undefined;

                if (key && currentKey === key) {
                    if (updatedNotification && typeof updatedNotification === "object") {
                        state.items[index] = {
                            ...current,
                            ...updatedNotification,
                        };
                        ensureNotificationMarkedAsRead(state.items[index]);
                    } else {
                        ensureNotificationMarkedAsRead(current);
                    }
                    state.unreadCount = Math.max(0, state.unreadCount - 1);
                    return;
                }
            }

            if (updatedNotification && typeof updatedNotification === "object") {
                ensureNotificationMarkedAsRead(updatedNotification);
                state.items.unshift(updatedNotification);
            }
        });
        builder.addCase(markNotificationRead.rejected, (state, action) => {
            const { notificationId } = action.meta.arg ?? {};
            if (notificationId !== undefined && notificationId !== null) {
                delete state.loading.markRead[String(notificationId)];
            }

            if (action.error && typeof action.error.message === "string") {
                state.error = action.error.message;
            }
        });

        builder.addCase(markAllNotificationsRead.pending, (state) => {
            state.loading.markAllRead = true;
            state.error = null;
        });
        builder.addCase(markAllNotificationsRead.fulfilled, (state) => {
            state.loading.markAllRead = false;
            state.items = state.items.map((item) => {
                const nextItem = { ...item };
                ensureNotificationMarkedAsRead(nextItem);
                return nextItem;
            });
            state.unreadCount = 0;
        });
        builder.addCase(markAllNotificationsRead.rejected, (state, action) => {
            state.loading.markAllRead = false;
            state.error = typeof action.error.message === "string"
                ? action.error.message
                : "Không thể đọc tất cả thông báo.";
        });

        builder.addCase(getUnreadNotificationsCount.pending, (state) => {
            state.loading.unreadCount = true;
        });
        builder.addCase(getUnreadNotificationsCount.fulfilled, (state, action) => {
            state.loading.unreadCount = false;
            const payload = action.payload as any;
            const count = parseNumber(payload?.count ?? payload?.data?.count ?? payload?.payload?.count ?? payload?.meta?.count);
            state.unreadCount = count !== undefined && count >= 0 ? count : 0;
        });
        builder.addCase(getUnreadNotificationsCount.rejected, (state) => {
            state.loading.unreadCount = false;
        });

    },
});

export const { resetNotificationsState, setNotificationsPageSize } = notificationsSlice.actions;
export default notificationsSlice.reducer;
